<template>
  <div class="h-full flex flex-col">
    <div class="grid grid-cols-6 grid-rows-2 lg:grid-rows-1 gap-2 mb-2">
      <div class="col-span-6 row-span-1 lg:col-span-3">
        <v-data-table :headers="header" :items="data">
          <template slot="progress">
          </template>

          <template slot="loading">
            <div class="flex flex-row justify-center">
              <SpinnerLoading class="z-50 text-gray-400" />
              <span>{{ $t('loading_properties') }}...</span>
            </div>
          </template>

          <template slot="no-data">
            <div class="flex flex-row justify-center">
              <span>{{ $t('none_found') }}.</span>
            </div>
          </template>
          <template v-slot:[`item.age`]="{ item }">
            <div>
              {{ item.age ? (+item.age).toFixed(2) : '--' }}
            </div>
          </template>
          <template v-slot:[`item.necessary_ration`]="{ item }">
            <div>
              {{ item.necessary_ration ? Math.max(+item.necessary_ration, 0).toFixed(2) : '--' }}
            </div>
          </template>
          <template v-slot:[`item.date`]="{ item }">
            <div>
              {{ getDateFormatted(item.date) }}
            </div>
          </template>
        </v-data-table>
      </div>
      <div class="col-span-6 row-span-1 lg:col-span-3">
        <ChartComponent :id="'graph_1'" type="xy" :graph-props="weightAndPrediction.props || {}" />
        <div style="display: flex;justify-content: flex-start;gap:10px">
          <span style="display: flex;justify-content: flex-start;align-items: center;">
            <span
              style="width: 25px;display: inline-block;margin-right: 5px;height: 10px;background-color: #3171ff">&nbsp;</span>
            {{ $t('weight') }}
          </span>
          <span style="display: flex;justify-content: flex-start;align-items: center;">
            <span
              style="width: 25px;display: inline-block;margin-right: 5px;height: 10px;background-color: #D6B133">&nbsp;</span>
            {{ $t('forecast') }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

import SpinnerLoading from '@/components/ui/SpinnerLoading.vue';
import format from 'date-fns/format';
import ChartComponent from "@/components/dashboard/ChartHighcharts";

export default {
  name: 'SlaughterForecastDashboardView',
  components: { ChartComponent },
  data() {
    return {
      data: []
    };
  },
  props: {
    loading: Boolean,
  },
  computed: {
    ...mapGetters('environment', [
      'getSelectedBatch',
      'getSelectedShed',
    ]),
    header() {
      return [
        { text: this.$t('age'), value: 'age', align: 'center', sortable: false },
        { text: this.$t('date'), value: 'date', align: 'center', sortable: false },
        { text: this.$t('weight_prediction'), value: 'weight', align: 'center', sortable: false },
        { text: this.$t('accumulated_consumption'), value: 'accumulated_consumption', align: 'center', sortable: false },
        { text: this.$t('need_for_feed'), value: 'necessary_ration', align: 'center', sortable: false },
      ];
    },
    weightAndPrediction: {
      get() {
        const serie_1 = this.data || []
        const funcT = (value) => this.$t(value)
        const getDateFormatted = (value) => this.getDateFormatted(value)
        return {
          id: 'history',
          props: {
            periodFrom: null,
            periodTo: null,
            legend: { enabled: false },
            title: {
              text: 'Peso e Projeção'
            },
            yAxis: {
              title: {
                text: 'Predição de peso (%)',
                enabled: false,
              },
              gridLineColor: '#cccccc',
              gridLineWidth: 0.5,
              labels: {
                formatter: function () {
                  return this.value + ' g';
                }
              }
            },
            credits: {
              enabled: false
            },
            tooltip: {
              borderColor: '#2c3e50',
              shared: true,
              formatter: function () {
                const value = serie_1.find(serie => {
                  return new Date(serie.date).getTime() === this.x
                })

                return this.points.map(item => {
                  let rt = ''

                  for (let i of Object.keys(value)) {
                    if (['age', 'date', 'weight', 'accumulated_consumption', 'necessary_ration'].indexOf(i) == -1) {
                      rt += '<span><strong>' + i + ':</strong> ' + (value?.[i] || 0) + '</span>' + '<br/>'
                    }
                  }
                  if (value.age != undefined) {
                    rt += '<span><strong>' + funcT('age') + ':</strong> ' + (value.age || 0) + '</span>' + '<br/>'
                  }
                  if (value.date != undefined) {
                    rt += '<span><strong>' + funcT('date') + ':</strong> ' + getDateFormatted(value.date) + '</span>' + '<br/>'
                  }
                  if (value.weight != undefined) {
                    rt += '<span><strong>' + funcT('weight_prediction') + ':</strong> ' + (value.weight || 0) + '</span>' + '<br/>'
                  }
                  if (value.accumulated_consumption != undefined) {
                    rt += '<span><strong>' + funcT('accumulated_consumption') + ':</strong> ' + (value.accumulated_consumption || 0) + '</span>' + '<br/>'
                  }
                  if (value.necessary_ration != undefined) {
                    rt += '<span><strong>' + funcT('need_for_feed') + ':</strong> ' + (value.necessary_ration || 0) + '</span>' + '<br/>'
                  }
                  return rt;

                }
                ).join('')
              }
            },
            plotOptions: {
              line: {
                marker: {
                  radius: 2
                }
              },
              column: {
                pointWidth: 20
              }
            },
            xAxis: {
              type: 'date',
              labels: {
                formatter: function () {
                  return format(new Date(this.value), 'dd/MM')
                },
              },
              gridLineColor: '#cccccc',
              gridLineWidth: 0.5
            },
            series: [
              { name: 'Peso', type: 'line', data: serie_1.map(item => ([new Date(item.date).getTime(), +item.weight])), zones: this.getZones(), zoneAxis: 'x' },
            ],
          }
        }
      }
    },
  },
  methods: {
    ...mapGetters('pcpPlanning', ['getSlaughterWeightProjection']),
    ...mapActions('pcpPlanning', ['loadSlaughterWeightProjection']),
    getZones() {
      const currentDate = new Date().getTime();
      return [
        {
          value: currentDate,
          color: '#3171ff'
        },
        {
          color: '#D6B133'
        }
      ]
    },
    getDateFormatted(date) {
      try {
        return format(new Date(date), 'dd/MM/yyyy HH:mm');
      } catch (err) {
        return '--';
      }
    },
  },
  watch: {
    'getSelectedBatch.id': {
      immediate: true,
      async handler(batchId) {
        if (!batchId) return;
        this.$emit('set-loading', true)
        await this.loadSlaughterWeightProjection({ batch_id: batchId })
        this.data = this.getSlaughterWeightProjection() ?? []
        this.$emit('set-loading', false);
      }
    }
  }
};
</script>

<style>
.h-dashboard-pcp {
  height: 828px !important;
}

@media(max-width: 888px) {
  .h-dashboard-pcp {
    height: 1492px !important;
  }
}
</style>
